import { useState } from "react";
import { Link2, Loader2, Shield, Upload } from "lucide-react";
import {
  ONCHAIN_EXCLUDED_FIELDS,
  ONCHAIN_PUBLISHED_FIELDS,
} from "@crewledger/shared/lib/onchain";
import type { PayPeriodReview } from "@crewledger/shared/schemas";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { generateOnchainMerkleRoot, publishOnchainProof } from "./api";
import { useOnchainPermissions } from "./use-onchain-permissions";

interface OnchainProofPanelProps {
  payPeriodId: string;
  integrity: PayPeriodReview["integrity"];
  onUpdated: () => void;
  onError: (message: string) => void;
  onSuccess: (message: string) => void;
}

function statusLabel(status: PayPeriodReview["integrity"]["onchainProofStatus"]): string {
  switch (status) {
    case "PUBLISHED":
      return "Published";
    case "PENDING":
      return "Root generated";
    case "FAILED":
      return "Publish failed";
    default:
      return "Not generated";
  }
}

function statusClass(status: PayPeriodReview["integrity"]["onchainProofStatus"]): string {
  switch (status) {
    case "PUBLISHED":
      return "bg-green-50 text-green-700 border-green-200";
    case "PENDING":
      return "bg-blue-50 text-blue-700 border-blue-200";
    case "FAILED":
      return "bg-red-50 text-red-700 border-red-200";
    default:
      return "bg-slate-50 text-slate-700 border-slate-200";
  }
}

export function OnchainProofPanel({
  payPeriodId,
  integrity,
  onUpdated,
  onError,
  onSuccess,
}: OnchainProofPanelProps) {
  const { featureEnabled, canManage } = useOnchainPermissions();
  const [generating, setGenerating] = useState(false);
  const [publishing, setPublishing] = useState(false);

  const handleGenerate = async () => {
    if (!canManage) return;
    setGenerating(true);
    try {
      const { data } = await generateOnchainMerkleRoot(payPeriodId);
      onSuccess(`Merkle root generated for ${data.workerCount} workers`);
      onUpdated();
    } catch (err) {
      onError(err instanceof Error ? err.message : "Failed to generate Merkle root");
    } finally {
      setGenerating(false);
    }
  };

  const handlePublish = async () => {
    if (!canManage) return;
    setPublishing(true);
    try {
      await publishOnchainProof(payPeriodId);
      onSuccess("Onchain proof published");
      onUpdated();
    } catch (err) {
      onError(err instanceof Error ? err.message : "Failed to publish proof");
    } finally {
      setPublishing(false);
    }
  };

  const canPublish =
    featureEnabled &&
    canManage &&
    integrity.onchainProofStatus === "PENDING" &&
    Boolean(integrity.merkleRootPreview);

  return (
    <div className="rounded-lg border border-navy-deep/10 bg-card-white p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2">
          <Shield className="h-5 w-5 text-navy-blueprint" />
          <div>
            <h4 className="font-semibold text-navy-deep">Proof Layer</h4>
            <p className="text-xs text-muted-foreground">Privacy-safe Merkle anchoring</p>
          </div>
        </div>
        <span
          className={cn(
            "rounded-full border px-2 py-0.5 text-xs font-semibold",
            statusClass(integrity.onchainProofStatus),
          )}
        >
          {statusLabel(integrity.onchainProofStatus)}
        </span>
      </div>

      <div className="mt-4 space-y-3 text-sm">
        {integrity.merkleRootPreview ? (
          <div className="rounded-md border border-dashed border-navy-blueprint/20 bg-navy-deep/5 px-3 py-2">
            <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Merkle root</p>
            <p className="mt-0.5 font-mono text-xs text-navy-deep">{integrity.merkleRootPreview}</p>
          </div>
        ) : null}

        {integrity.payPeriodPublicRef ? (
          <div className="flex justify-between gap-3">
            <span className="text-muted-foreground">Public reference</span>
            <span className="font-mono text-xs">{integrity.payPeriodPublicRef}</span>
          </div>
        ) : null}

        {integrity.transactionSignaturePreview ? (
          <div className="flex justify-between gap-3">
            <span className="flex items-center gap-1 text-muted-foreground">
              <Link2 className="h-3.5 w-3.5" />
              Transaction
            </span>
            <span className="font-mono text-xs">{integrity.transactionSignaturePreview}</span>
          </div>
        ) : null}

        {integrity.publishedAt ? (
          <div className="flex justify-between gap-3 text-xs text-muted-foreground">
            <span>Published</span>
            <time>{new Date(integrity.publishedAt).toLocaleString()}</time>
          </div>
        ) : null}
      </div>

      <div className="mt-4 rounded-md bg-bg-light/80 px-3 py-3 text-xs text-muted-foreground">
        <p className="font-semibold text-navy-deep">Privacy model</p>
        <p className="mt-1">
          Werkio never publishes employee names, pay rates, raw hours, notes, or equipment details onchain.
          Optional proof uses hashed pay-period records and Merkle roots only.
        </p>
        <div className="mt-2 grid gap-2 sm:grid-cols-2">
          <div>
            <p className="font-medium text-navy-deep">Never published</p>
            <ul className="mt-1 list-inside list-disc">
              {ONCHAIN_EXCLUDED_FIELDS.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          </div>
          <div>
            <p className="font-medium text-navy-deep">Published onchain</p>
            <ul className="mt-1 list-inside list-disc">
              {ONCHAIN_PUBLISHED_FIELDS.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      <div className="mt-4 flex flex-wrap gap-2">
        <Button
          variant="outline"
          size="sm"
          disabled={!featureEnabled || !canManage || generating}
          onClick={() => void handleGenerate()}
        >
          {generating ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
          Generate private Merkle root
        </Button>
        <Button
          size="sm"
          disabled={!canPublish || publishing}
          onClick={() => void handlePublish()}
        >
          {publishing ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Upload className="h-4 w-4" />
          )}
          Publish proof
        </Button>
      </div>

      {!featureEnabled ? (
        <p className="mt-3 text-xs text-amber-700">
          Onchain proofs are disabled (`ENABLE_ONCHAIN_PROOFS=false`). Publish is unavailable until
          the feature flag is enabled.
        </p>
      ) : !canManage ? (
        <p className="mt-3 text-xs text-muted-foreground">
          Only company owners can generate and publish onchain proofs.
        </p>
      ) : null}
    </div>
  );
}
